import { copyJsonObject } from "../commonUtils";
import { isPendingBid } from "./bidCommon";
import { BID_TYPE } from "./bidConstants";

export const jumpToSearchedEmployee = (apiRef, currSearchedEmployeesIdx, searchedEmployees, bidGroup) => {
  if (currSearchedEmployeesIdx !== -1) {
    const currEid = searchedEmployees[currSearchedEmployeesIdx].eid;

    // Wait for the scroll to complete, then scroll to the item
    setTimeout(() => {
      apiRef.current.scrollToIndexes({
        rowIndex: bidGroup.findIndex((emp) => emp.eid === currEid),
      });
    }, 0);

    // Scroll item to the top, we can scroll using data-id or data-rowindex that datagrid is providing
    setTimeout(() => {
      const rowElem = document.querySelector(`[data-id="${currEid}"]`);
      if (rowElem) {
        rowElem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 50); // Delay 50 to find data-id in the DOM to load after scrollToIndexes call
  }
};

export const getEligibleEmployees = (bidGroup) => {
  return bidGroup?.filter((employee) => employee.isEligible === true && !employee.isRemoved);
};

export const getIneligibleEmployees = (bidGroup) => {
  return bidGroup?.filter((employee) => employee.isEligible === false && !employee.isRemoved);
};

export const getEmployeeUnrankedCount = (employees) => {
  return employees.filter((employee) => employee.rank === "-").length;
};

export const getRemovedEmployees = (bidGroup) => {
  return bidGroup.filter((emp) => emp.isRemoved);
};

export const getNonRemovedEmployees = (bidGroup) => {
  return bidGroup.filter((emp) => !emp.isRemoved);
};

export const splitRankedAndUnrankedEmployees = (bidGroup) => {
  const copiedBidGroup = copyJsonObject(bidGroup);
  const rankedEmployees = copiedBidGroup.filter((emp) => emp.rank !== "-");
  let unrankedAddedEmployees = copiedBidGroup.filter((emp) => emp.rank === "-" && !emp.isRemoved);

  return { rankedEmployees, unrankedAddedEmployees };
};

export const updateRowPosition = (initialIndex, newIndex, rows) => {
  const rowsClone = [...rows];
  const row = rowsClone.splice(initialIndex, 1)[0];
  rowsClone.splice(newIndex, 0, row);
  return rowsClone;
};

export const updateRanks = (rows) => {
  rows.forEach((item, index) => (item.rank = index + 1));
};

export const handleRowOrderChange = async (params, bidGroup, setBidGroup) => {
  let { rankedEmployees, unrankedAddedEmployees } = splitRankedAndUnrankedEmployees(bidGroup);

  //1 Reordering added employee
  const reorderingUnrankedEmployee = unrankedAddedEmployees.find((emp) => emp.eid === params.row.eid);
  if (reorderingUnrankedEmployee !== undefined) {
    //Moving him into ranked Employees to get ranked
    rankedEmployees.unshift(reorderingUnrankedEmployee);
    unrankedAddedEmployees = [];
  }

  //2 moving employee to the top of unranked added employee
  if (params.targetIndex === 0 && unrankedAddedEmployees.length > 0) {
    rankedEmployees.unshift(...unrankedAddedEmployees);
    unrankedAddedEmployees = [];
  }

  //3 assign indexes based on unranked Added employees existence
  const oldIndex = unrankedAddedEmployees.length > 0 ? params.oldIndex - 1 : params.oldIndex;
  const targetIndex = unrankedAddedEmployees.length > 0 ? params.targetIndex - 1 : params.targetIndex;
  const newRows = updateRowPosition(oldIndex, targetIndex, rankedEmployees);

  updateRanks(newRows);
  setBidGroup([...unrankedAddedEmployees, ...newRows, ...getRemovedEmployees(bidGroup)]);
};

export const handleRankCellEditCommit = (params, e, bidGroup, setBidGroup, setInvalidBidGroupRankDialog, apiRef) => {
  const { field, id, value } = params; //field:columnName, id:eId, value:newRankValue
  let { rankedEmployees, unrankedAddedEmployees } = splitRankedAndUnrankedEmployees(bidGroup);
  const selectedEmployeeForRanking = bidGroup?.find((item) => item.eid === id);

  // Changing rank of unranked employee
  let changingRankOfUnRankedEmployee = unrankedAddedEmployees.find((emp) => emp.eid === id);
  if (changingRankOfUnRankedEmployee !== undefined) {
    //Moving him into ranked Employees to get ranked
    rankedEmployees.unshift(changingRankOfUnRankedEmployee);
    unrankedAddedEmployees = [];
  }
  if (field === "rank" && value > 0 && value <= rankedEmployees.length) {
    const newRows = updateRowPosition(selectedEmployeeForRanking?.rank - 1, value - 1, rankedEmployees);
    updateRanks(newRows);
    setBidGroup([...unrankedAddedEmployees, ...newRows, ...getRemovedEmployees(bidGroup)]);
    if (selectedEmployeeForRanking.rank !== value) {
      apiRef.current.scrollToIndexes({
        rowIndex: unrankedAddedEmployees.length > 0 ? value : value - 1,
      });
    }
    setInvalidBidGroupRankDialog(false);
  } else if (
    field !== "rank" ||
    (value === "-" && selectedEmployeeForRanking?.rank === "-") ||
    (value === "" && (e.code === "Backspace" || e.code === "Delete")) ||
    value === undefined
  ) {
    setInvalidBidGroupRankDialog(false);
  } else {
    setInvalidBidGroupRankDialog(true);
  }
};

export const buildAddEmployeeData = (employeeData, comments, bidStatus, bidType) => {
  const newEmployeeData = scrubAddEmployeeData(employeeData);
  newEmployeeData.comments = comments;
  newEmployeeData.isEligible = true;
  newEmployeeData.isEligibleAtOpen = isPendingBid(bidStatus);
  newEmployeeData.isManuallyAddedToBidGroup = true;
  newEmployeeData.rank = "-";
  if (bidType === BID_TYPE.FTO) {
    newEmployeeData.balance = "-";
  }
  if (bidType === BID_TYPE.TWU_DAT_FTO) {
    newEmployeeData.ftoBalance = "-";
    newEmployeeData.datBalance = "-";
  }
  if (bidType === BID_TYPE.IAM_DAT) {
    newEmployeeData.datBalance = "-";
  }

  if (bidType === BID_TYPE.NC_PTO_FTO) {
    newEmployeeData.ptoBalance = "-";
    newEmployeeData.ftoBalance = "-";
  }

  if (bidType === BID_TYPE.NC_PTO_GOC_HOL) {
    newEmployeeData.ptoBalance = "-";
    newEmployeeData.gocHolBalance = "-";
  }

  return newEmployeeData;
};

const scrubAddEmployeeData = (employeeData) => {
  const newAddEmployeeData = {
    eid: employeeData.eid,
    homeStation: employeeData.homeStation,
    homeAccountStation: employeeData.homeAccountStation,
    homeAccountJobCode: employeeData.homeAccountJobCode,
    firstName: employeeData.firstName,
    lastName: employeeData.lastName,
    shortName: employeeData.shortName,
    ftpt: employeeData.ftpt,
    isTemp: !!employeeData.tempStation,
    seniorityDate: employeeData.seniorityDate ? employeeData.seniorityDate : "",
    birthDate: employeeData.birthDate ? employeeData.birthDate : "",
    phoneNumber: employeeData.phoneNumber ? employeeData.phoneNumber : "",
    seniorityTiebreakerDate: employeeData.seniorityTiebreakerDate ? employeeData.seniorityTiebreakerDate : "",
    seniorityTiebreakerStation: employeeData.seniorityTiebreakerStation ? employeeData.seniorityTiebreakerStation : "",
    jobTitle: employeeData.jobTitle ? employeeData.jobTitle : "",
    hireDate: employeeData.hireDate ? employeeData.hireDate : "",
    classificationDate: employeeData.classificationDate ? employeeData.classificationDate : "",
    contractDate: employeeData.contractDate ? employeeData.contractDate : "",
    dualSeniorityDate: employeeData.dualSeniorityDate ? employeeData.dualSeniorityDate : "",
    wnSliSeniorityDate: employeeData.wnSliSeniorityDate ? employeeData.wnSliSeniorityDate : "",
    flAtlSeniorityDate: employeeData.flAtlSeniorityDate ? employeeData.flAtlSeniorityDate : "",
  };
  if (employeeData.standardRelief) {
    newAddEmployeeData.standardRelief = employeeData.standardRelief;
  }
  return newAddEmployeeData;
};
